"use client";

import { createContext, useContext } from "react";

/**
 * Cookie同意の状態
 *
 * - `"granted"`: ユーザーが明示的に同意した状態（GA4のanalytics_storageが有効化される）
 * - `"denied"`: ユーザーが明示的に拒否した状態（GA4はcookieless pingのみ送信）
 * - `null`: 未選択（初回訪問でバナー表示中、またはlocalStorage利用不可）
 *
 * tracking/ga4.ts の `ConsentState` (`"granted" | "denied"`) とは別名。
 * 未選択状態(null)を含むかどうかが異なる。
 */
type CookieConsent = "granted" | "denied" | null;

type CookieConsentContextValue = {
  /**
   * Cookie同意UI（バナー / 設定）が利用可能か。
   * GA4 measurementIdなど計測基盤の有無を抽象化した値で、entrypoint(layout)から注入される。
   * falseの場合は同意UI自体を出さない（同意を取っても送信先がないため）。
   */
  available: boolean;
  consent: CookieConsent;
  grant: () => void;
  deny: () => void;
};

export const CookieConsentContext =
  createContext<CookieConsentContextValue | null>(null);

/**
 * Cookie同意状態を参照するためのフック。
 * `CookieConsentProvider` 配下でのみ利用可能。
 */
export function useCookieConsent(): CookieConsentContextValue {
  const value = useContext(CookieConsentContext);
  if (value === null) {
    throw new Error(
      "useCookieConsent は CookieConsentProvider の配下で呼び出してください",
    );
  }
  return value;
}
